import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Panel } from "@/components/portal/widgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, MapPinOff, MapPin, CheckCircle2, Pencil, Sparkles } from "lucide-react";
import { fmtDateTime } from "@/lib/time";

const LABEL = { pickup_address: "Pickup", dropoff_address: "Drop-off" };

const AiPick = ({ f, plan, onDone }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const a = plan.assignments?.[0];
  const u = plan.unassignable?.[0];
  const confirm = async () => {
    setBusy(true);
    try {
      await fleetApi.post("/trips/assign-confirm", { assignments: [a] });
      toast.success(`Assigned to ${a.driver_name || "driver"}.`);
      onDone(f.id);
    } catch (e) { toast.error(fleetError(e, "Couldn't confirm the assignment.")); setBusy(false); }
  };
  return (
    <div className="mt-3 flex flex-col gap-2 rounded-md border border-blue-500/30 bg-blue-500/5 px-3 py-2.5 text-sm sm:flex-row sm:items-center" data-testid={`address-ai-pick-${f.trip_id}`}>
      <Sparkles className="h-4 w-4 shrink-0 text-blue-400" />
      <span className="flex-1 text-slate-200">{a ? <>Address fixed — back in AI assignment. AI suggests <b className="text-white">{a.driver_name || a.driver_id}</b>{a.reason ? ` (${a.reason})` : ""}.</> : `Address fixed — back in AI assignment, but AI couldn't match a driver: ${u?.reason || "no driver available"}.`}</span>
      {a && <Button size="sm" disabled={busy} onClick={confirm} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid={`address-ai-confirm-${f.trip_id}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Confirm"}</Button>}
      {!a && <Button size="sm" variant="ghost" onClick={() => navigate(`/portal/dispatch?q=${encodeURIComponent(f.external_trip_id || "")}`)} className="text-blue-300 hover:bg-slate-800" data-testid={`address-ai-manual-${f.trip_id}`}>Assign manually</Button>}
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => onDone(f.id)} className="text-slate-400 hover:bg-slate-800 hover:text-white" data-testid={`address-ai-skip-${f.trip_id}`}>Later</Button>
    </div>
  );
};

const FlagRow = ({ f, onDone }) => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(f.suggestion || f.raw);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const backToAi = async () => {
    if (user?.role !== "owner") return false;
    try {
      const { data } = await fleetApi.post("/trips/assign-plan", { trip_ids: [f.trip_id] });
      if (!data.assignments?.length && !data.unassignable?.length) return false;
      setPlan(data);
      return true;
    } catch { return false; }
  };
  const act = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/addresses/flags/${encodeURIComponent(f.id)}/resolve`, { action, value: action === "edit" ? value : undefined });
      if (action === "keep") toast.success("Kept the original address.");
      else if (data.resolution?.still_flagged) toast.warning("Saved to the trip, but the new address still looks wrong. Please double-check it.");
      else toast.success("Address updated on the trip. Drivers see it right away.");
      if (data.resolution?.still_flagged || !(await backToAi())) onDone(f.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const bad = f.status === "invalid";
  return (
    <li className="px-5 py-4" data-testid={`address-flag-${f.trip_id}-${f.field}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${bad ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>{bad ? "Wrong address" : "Check address"}</span>
        <Link to={`/portal/trips/${f.trip_id}`} className="font-semibold text-white hover:text-blue-300 hover:underline" data-testid={`address-flag-open-${f.trip_id}-${f.field}`}>{f.rider_name}</Link>
        <span className="font-mono-data text-xs text-slate-500">{f.external_trip_id} · {LABEL[f.field]} · {fmtDateTime(f.scheduled_pickup)}</span>
      </div>
      <p className="mt-1.5 text-xs text-slate-400">{f.reason}</p>
      <div className="mt-2 grid gap-2 text-sm md:grid-cols-2">
        <div className="rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">Imported</div><div className="text-slate-300">{f.raw}</div></div>
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-emerald-400">Suggested</div><div className="text-white" data-testid={`address-suggestion-${f.trip_id}-${f.field}`}>{f.suggestion || "No match found — please edit"}</div></div>
      </div>
      {plan ? <AiPick f={f} plan={plan} onDone={onDone} /> : editing ? (
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <Input value={value} onChange={(e) => setValue(e.target.value)} className="border-slate-700 bg-slate-900 text-white" data-testid={`address-edit-input-${f.trip_id}-${f.field}`} />
          <Button size="sm" disabled={busy || !value.trim()} onClick={() => act("edit")} className="bg-blue-700 text-white hover:bg-blue-800" data-testid={`address-edit-save-${f.trip_id}-${f.field}`}>Save</Button>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          {f.suggestion && <Button size="sm" disabled={busy} onClick={() => act("accept")} className="gap-1 bg-emerald-600 text-white hover:bg-emerald-500" data-testid={`address-accept-${f.trip_id}-${f.field}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Use suggestion</Button>}
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(true)} className="gap-1 text-blue-300 hover:bg-slate-800" data-testid={`address-edit-${f.trip_id}-${f.field}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => act("keep")} className="text-slate-400 hover:bg-slate-800 hover:text-white" data-testid={`address-keep-${f.trip_id}-${f.field}`}>Keep original</Button>
        </div>
      )}
    </li>
  );
};

export const AddressCheckPanel = ({ state, onResolved }) => {
  if (!state) return null;
  if (state.loading) return <Panel title="Checking addresses…" testid="address-check-loading"><div className="flex items-center gap-2 px-5 py-6 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin text-blue-400" /> Validating pickup and drop-off addresses with Google…</div></Panel>;
  if (state.error) return <Panel title="Address check" testid="address-check-error"><p className="px-5 py-5 text-sm text-amber-300">{state.error}</p></Panel>;
  const flags = state.flags || [];
  const c = state.counts;
  return (
    <Panel title={`Address check · ${flags.length} to review`} testid="address-check-panel">
      {c && (
        <div className="flex flex-wrap gap-2 px-5 pt-4 text-xs" data-testid="address-check-summary">
          <span className="rounded-full bg-emerald-500/10 px-3 py-1 font-semibold text-emerald-300"><MapPin className="mr-1 inline h-3 w-3" />{c.valid} confirmed</span>
          <span className="rounded-full bg-amber-500/10 px-3 py-1 font-semibold text-amber-300">{c.suspect} need a look</span>
          <span className="rounded-full bg-red-500/10 px-3 py-1 font-semibold text-red-300"><MapPinOff className="mr-1 inline h-3 w-3" />{c.invalid} wrong</span>
          {c.unchecked > 0 && <span className="rounded-full bg-slate-700/40 px-3 py-1 font-semibold text-slate-300">{c.unchecked} not checked</span>}
        </div>
      )}
      {flags.length === 0
        ? <p className="px-5 py-6 text-sm text-emerald-300" data-testid="address-check-clean">All addresses look good.</p>
        : <ul className="mt-2 divide-y divide-slate-800">{flags.map((f) => <FlagRow key={f.id} f={f} onDone={onResolved} />)}</ul>}
    </Panel>
  );
};
